// src/Pages/Kayttoehdot/OhjeetJaEhdot.jsx
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Check, AlertTriangle, Shield, Calendar, Info, FileText, Star } from "lucide-react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";

/* Yleinen callout-boksi */
function Callout({ tone = "info", title, children }) {
  const tones = {
    info:    "bg-white/70 dark:bg-black/50 ring-black/10 dark:ring-white/10",
    warn:    "bg-yellow-100/70 dark:bg-yellow-900/30 ring-yellow-300/60 dark:ring-yellow-800/60",
    danger:  "bg-red-100/70 dark:bg-red-500/10 ring-red-300/60 dark:ring-red-600/50",
    success: "bg-emerald-100/70 dark:bg-emerald-900/20 ring-emerald-300/60 dark:ring-emerald-700/50",
  };
  const Icon = tone === "danger" ? AlertTriangle : tone === "success" ? Check : Info;
  return (
    <div className={`rounded-xl ring p-4 md:p-5 ${tones[tone] || tones.info}`}>
      {title && (
        <div className="flex items-center gap-2 mb-2 text-black/80 dark:text-white/90">
          <Icon size={18} className="opacity-80" />
          <h3 className="font-semibold">{title}</h3>
        </div>
      )}
      <div className="text-black/80 dark:text-white/80 leading-relaxed text-sm md:text-base">
        {children}
      </div>
    </div>
  );
}

export default function OhjeetJaEhdot() {
  const location = useLocation();

  const FIRST_TIME_FLAG = "bbx:onb:legal_seen_v1";

  // Ankkurirullaus
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  // Onboardingista paluu
  const showBackToAccept = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if ((sp.get("from") || "").toLowerCase() === "onboarding") return true;
    if (location.state?.fromOnboarding === true) return true;
    if (typeof document !== "undefined" && /\/onboarding(\b|\/|$)/.test(document.referrer || "")) return true;
    return false;
  }, [location.search, location.state]);

  useEffect(() => {
    const fromOnb = showBackToAccept && (localStorage.getItem(FIRST_TIME_FLAG) !== "1");
    if (fromOnb) localStorage.setItem(FIRST_TIME_FLAG, "1");
  }, [showBackToAccept]);

  const allowBackButton = showBackToAccept && localStorage.getItem(FIRST_TIME_FLAG) !== "1";

  const sections = useMemo(
    () => [
      { id: "ohjeet",       label: "Käyttöohjeet (pikaopas)" },
      { id: "mita-saat",    label: "Mitä saat palvelusta" },
      { id: "varausmaksu",  label: "Varausmaksu" },
      { id: "velvollisuudet", label: "Esiintyjän velvollisuudet" },
      { id: "kalenteri",    label: "Kalenterin käyttö" },
      { id: "arvostelut",   label: "Arvostelut & näkyvyys" },
      { id: "saannot",      label: "Pelisäännöt ja kiellot" },
      { id: "viestinta",    label: "Viestintä ja ammattimaisuus" },
      { id: "peruutukset",  label: "Peruutusehdot" },
      { id: "ehdot",        label: "Käyttöehdot (täysi sisältö)" },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40" data-page="legal">
      <div className="mx-auto max-w-7xl px-4 pt-24 pb-20">
        {/* HERO */}
        <header className="mb-8 md:mb-12 text-center">
          <h1 className="font-customFont2 text-2xl sm:text-3xl md:text-4xl font-bold text-black/90 dark:text-white/90">
            Käyttöohjeet & käyttöehdot
          </h1>
          <p className="mt-3 max-w-3xl mx-auto text-black/70 dark:text-white/70 text-sm md:text-base">
            Yhdistimme ohjeet ja ehdot yhdeksi selkeäksi sivuksi. Tutustu sisältöön rauhassa.
          </p>
        </header>

        {/* CONTENT GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* SIDE NAV */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="sticky top-24 rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 p-4">
              <p className="text-xs uppercase tracking-wide text-black/90 dark:text-white/60 mb-2 font-semibold">Pikaohje</p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block rounded-lg px-3 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5 text-black/80 dark:text-white/80"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
              <div className="mt-4 space-y-2">
                {allowBackButton && (
                  <NeonButton as={Link} to="/onboarding" variant="primary" size="sm" className="w-full">
                    Palaa hyväksymään
                  </NeonButton>
                )}
                <NeonButton as={Link} to="/" variant="ghost" size="sm" className="w-full">
                  Etusivu
                </NeonButton>
              </div>
            </div>
          </aside>

          {/* MAIN */}
          <main className="lg:col-span-8 xl:col-span-9 space-y-6">
            {/* --- KÄYTTÖOHJEET --- */}
          <section
            id="ohjeet"
            className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5"
          >
            <h2 className="text-xl font-semibold mb-3 text-black/90 dark:text-white/90">
              Käyttöohjeet (pikaopas)
            </h2>

            {/* Esiintyjän puoli */}
            <p className="text-black/80 dark:text-white/80 mb-3">
              <strong>Esiintyjälle:</strong> määritä maakunnittain <strong>settipituudet</strong> ja niille <strong>hinnat</strong> sekä avaa <strong>vapaat päivät</strong> kalenteriin (vihreät). Kalenterissa
              <strong> tarjouspyynnöt</strong> näkyvät keltaisena ja <strong>vahvistetut varaukset</strong> punaisena.
            </p>

            {/* Asiakkaan puoli */}
            <p className="text-black/80 dark:text-white/80 mb-4">
              <strong>Asiakkaalle:</strong> siirry esiintyjän profiiliin, valitse <strong>vihreä päivä</strong> ja täytä lomake
              (nimi, tapahtumapaikka, puhelinnumero). Osoitteen perusteella järjestelmä tunnistaa <strong>maakunnan</strong> ja
              näyttää tälle alueelle asetetut <strong>settipituudet</strong>. Lomakkeen
              lähetys luo <strong>aktiivisen tarjouspyynnön</strong> ja järjestelmä lähettää sinulle tarjouksen sähköpostiin
              selkeänä könttäsummana. Esiintyjä saa myös ilmoituksen sähköpostitse. Tarjouspyyntö on voimassa <strong>1 tunnin</strong>.
            </p>

            <Callout tone="info" title="Vahvistus & ehdot">
              <p className="text-black/80 dark:text-white/80">
                Asiakas vahvistaa varauksen sähköpostissa painamalla <strong>Hyväksy</strong>-painiketta, jolloin hän hyväksyy
                Buukkerin{" "}
                <Link to="/kayttoehdot#peruutukset" className="underline text-pink dark:text-primary">
                  peruutusehdot
                </Link>
                . Vahvistuksesta tallennetaan varaukselle <strong>aikaleima ja tunnistetiedot</strong>. Jos vahvistusta ei tule
                yhden (1) tunnin kuluessa, pyyntö <strong>raukeaa</strong> ja poistuu esiintyjän kalenterista.
              </p>
            </Callout>
          </section>

          <section id="mita-saat" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
            <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">Mitä saat palvelusta</h3>
            <ul className="list-disc pl-5 space-y-2 text-black/80 dark:text-white/80">
              <li><b>Selkeä profiilisivu</b> omalla osoitteella (käytännössä oma verkkosivu).</li>
              <li><b>Hallintapaneeli</b>: musiikkityylit, soittimet, lisäpalvelut, some-linkit sekä YouTube- ja SoundCloud-upotukset.</li>
              <li>
                <b>Keikkailualueet ja omat hinnat alueittain</b>
              </li>
              <li><b>Dynaaminen kalenteri</b>: Tsekkaa asiakkaan tiedot, hinnat, matkakulut ja aktiiviset tarjouspyynnöt; klikkaile auki buukattavat päivät.</li>
              <li><b>Automaattinen matkakululaskenta</b> kotiosoitteesta edestakaisin (0,59 €/km).</li>
              <li><b>Automaattinen palautepyyntö</b> keikan jälkeen – reilua.</li>
              <li><b>Kuukauden esiintyjä</b> -nosto tähtipalautteiden perusteella.</li>
              <li><b>Tarjous asiakkaalle</b> selkeänä könttösummana.</li>
              <li><b>Ammattitaitoinen tuki</b>.</li>
              <li><b>Usean esiintymiskortin hallinta</b> yhdellä profiililla.</li>
              <li><b>Puskuriasetus</b>: määritä, millä varoitusajalla otat keikkoja vastaan.</li>
            </ul>
          </section>

          {/* VAIN YKSI tällainen osio tiedostossa (poista mahdollinen aiempi duplikaatti) */}
          <section id="maksutapa" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
            <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">Maksut ja tilitys</h3>
            <ul className="list-disc pl-5 space-y-2 text-black/80 dark:text-white/80">
              <li>
                Loppusumma (palkkion loppu + mahdolliset lisäpalvelut) sovitaan ja maksetaan suoraan esiintyjän ja asiakkaan välillä.
                Tarvittaessa Buukkeri voi hoitaa laskutuksen, jolloin välityspalkkio vähennetään tilityksestä.
              </li>
              <li>
                Hyväksynnässä veloitettava varausmaksu (15&nbsp;% / 12&nbsp;%) hyvitetään <b>loppusummasta</b>; <b>ei palauteta</b> peruutuksissa.
              </li>
              <li>Asiakkaan maksuaika on <b>14 päivää</b> esiintymisestä (ellei toisin sovita).</li>
              <li>Huolehdi omista vero- ja rekisteröintivelvoitteistasi (esim. Y-tunnus/ALV).</li>
            </ul>
          </section>


            {/* VARAUSMAKSU */}
            <section id="varausmaksu" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">
              Varausmaksu (ei palauteta)
            </h3>
            <p className="text-black/80 dark:text-white/80 mb-4">
              Hyväksynnän yhteydessä asiakkaalta veloitetaan <b>varausmaksu</b> 
              (bändit, duot &amp; DJ:t 15&nbsp;%, trubaduurit 12&nbsp;%).
              Varausmaksu <b>hyvitetään</b> loppusummasta, mutta sitä <b>ei palauteta</b> asiakkaan peruuttaessa.
              Jos peruutusehtojen mukainen peruutusmaksu syntyy, varausmaksu <b>vähennetään</b> siitä; 
              jos peruutusmaksu on 0&nbsp;€, varausmaksu jää palvelulle.
            </p>

              <Callout tone="warn" title="Kuluttajainfo">
                Etämyynnin lakisääteinen 14 vrk:n peruuttamisoikeus ei koske vapaa-ajan palveluja, joille on sovittu tietty ajankohta.
                Ehdot näytetään selkeästi ennen hyväksyntää ja vahvistetaan sähköpostitse.
              </Callout>
            </section>

            <section
              id="velvollisuudet"
              className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5 "
            >
              <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">
                Esiintyjän velvollisuudet
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-black/80 dark:text-white/80">
                <li>
                  <b>Hinnoittele settisi huolellisesti — asiakkaalle annettu tarjous on lupaus hinnasta.</b>
                </li>
                <li>
                  <b>
                    Hinnoittelun tulee olla totuudenmukaista ja linjassa muun julkisen hinnoittelusi kanssa.
                    Buukkeri seuraa alustalla ilmoitettuja hintoja, ja selvästi harhaanjohtava tai
                    kiusanteoksi/“näkyvyyshinnaksi” tulkittava hinnoittelu voi johtaa profiilin piilottamiseen
                    tai poistamiseen alustalta.
                  </b>
                </li>
                <li>
                  <b>Varmista, että saat ilmoitukset tarjouspyynnöistä; tarkista myös roskaposti.</b>
                </li>
                <li>
                  <b>
                    Pidä aina vähintään yksi peräkkäinen pe–la -jakso avoinna 30pv sisällä; muuten profiili
                    voidaan piilottaa. Buukkeri on buukkausalusta — vaikka tarjoamme ilmaisen profiilisivun,
                    palvelu ei ole pelkkään promokäyttöön.
                  </b>
                </li>
                <li>
                  <b>Pidetään homma asiallisena.</b>
                </li>
                <li>
                  <b>Saamasi palaute vaikuttaa kokonaisarvioon — hoida keikkasi kunnialla.</b>
                </li>
              </ul>
            </section>

            <section id="kalenteri" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-black/90 dark:text-white/90">
                <Calendar size={20} /> Kalenterin käyttö pähkinänkuoressa
              </h3>
            <ol className="list-decimal pl-5 space-y-2 text-black/80 dark:text-white/80">
                <li>Avaa päivät, kun olet vapaa esiintymään (vihreä).</li>
                <li>Vahvistettu varaus näkyy punaisena.</li>
                <li>Tarjouspyyntö vireillä näkyy keltaisena.</li>
                <li>
                  Määritä puskuri: millä varoitusajalla olet buukattavissa. Puskuri <b>0 pv</b> = näyt etusivun haussa
                  tänään; puskuri <b>7 pv</b> = näyt etusivun haussa viikon päästä — edellyttäen, että sinulla on
                  avattuja (vihreitä) päiviä silloin.
                </li>
                <li>Avaamasi (vihreät) päivät näkyvät heti profiilin kalenterissa.</li>
                <li>Vihreä päivä on buukattavissa; kun asiakas hyväksyy tarjouksen, sitoudut keikkaan.</li>
                <li>Klikkaamalla varattua (punaista) päivää näet keikan tiedot (asiakas, hinta, matkakulut jne.).</li>
                <li>Siirrä keikan tiedot Google-kalenteriisi helposti.</li>
                <li>Klikkaamalla keltaista näet aktiivisen tarjouspyynnön tiedot.</li>
                <li>Jos muutat puskuri-asetusta, muista tallentaa.</li>
              </ol>

            </section>

            {/* ARVOSTELUT & NÄKYVYYS */}
            <section id="arvostelut" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-black/90 dark:text-white/90">
                <Star size={20} /> Arvostelut & näkyvyys
              </h3>

              <p className="text-black/80 dark:text-white/80">
                <b>Palautepyyntö</b> lähtee automaattisesti keikan jälkeisenä päivänä tilaajan sähköpostiin. Viestissä on
                <b> PIN-koodi</b>, jolla tilaaja avaa palautelomakkeen, antaa <b>tähtiarvion (1–5)</b> ja kirjoittaa viestin.
                Annetut tähdet kertyvät profiilisi <b>kokonaisarvioon</b>, ja <b>arvostelujen määrä</b> näytetään tähtien vieressä.
              </p>

              <p className="text-black/80 dark:text-white/80 mt-2">
                Laadukkaat promomateriaalit (hyvät kuvat, videot, selkeä kuvaus) parantavat konversiota, ja
                <b> ammattimaisesti hoidetut keikat</b> nostavat arvosanaa&mdash;korkea arvosana kasvattaa näkyvyyttäsi hauissa.
                Buukkeri julkaisee kuukausittain etusivulla <b>Kuukauden esiintyjän</b> arvostelujen keskiarvon perusteella.
              </p>
            </section>


          <section id="saannot" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-black/90 dark:text-white/90">
              <Shield size={20} /> Pelisäännöt ja kiellot
            </h3>

            <Callout tone="danger" title="Alustan kiertäminen on kielletty">
              Buukkerin ohittaminen (“vetää välistä”) johtaa <b>välittömään tilin sulkemiseen</b>. Buukkerilla on oikeus periä
              vähintään <b>välityspalkkion suuruisen sopimussakon</b> buukatuista keikoista (perustuen tapahtuman kokonaishintaan),
              mahdolliset käsittelykulut sekä <b>korvaus</b> aiheutuneesta vahingosta.
            </Callout>

            {/* Ei erillisiä listakohtia tässä osiossa */}
          </section>

           <section id="viestinta" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">Viestintä & ammattimaisuus</h3>
              <ul className="list-disc pl-5 space-y-2 text-black/80 dark:text-white/80">
                <li>Pidä tiedot paikkansapitävinä (kuvaus, hinnat, alueet ja yhteystiedot).</li>
                <li>Pidä kiinni sovituista aikatauluista ja ilmoita viiveistä heti.</li>
                <li>Viestittele asiakkaan kanssa ammattimaisesti ja vastaa viesteihin viipymättä.</li>
                <li>Esiintymiset tehdään selvin päin.</li>
                <li>Ole tavoitettavissa.</li>
                <li>Buukkeri auttaa, jos jokin mietityttää.</li>
              </ul>
            </section>

           <section id="peruutukset" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h3 className="text-lg font-semibold mb-3 text-black/90 dark:text-white/90">Peruutusehdot</h3>

              <Callout tone="info" title="Hyväksyntä napista">
                Painamalla <b>“Hyväksy tarjous”</b> asiakas vahvistaa varauksen ja hyväksyy nämä peruutusehdot.
                Hyväksyntä (aikaleima ja tunnistetiedot) tallennetaan varaukselle.
              </Callout>

              {/* Jos asiakas peruu */}
              <div className="mt-4 text-black/80 dark:text-white/80">
                <h4 className="font-semibold mb-2">Jos asiakas peruu</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Peruutukset tehdään aina <b>kirjallisesti</b> (esim. sähköpostitse).</li>
                  <li>Peruutusmaksu määräytyy <b>kokonaishinnasta</b> ja aikarajat lasketaan esiintymispäivästä.</li>
                  <li><b>Varausmaksu on palauttamaton.</b></li>
                </ul>

                <p className="mt-3 font-semibold">Peruutusmaksu (yleinen)</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Peruutus ≥ 120 päivää ennen: <b>ei maksua</b></li>
                  <li>Peruutus 30–119 päivää ennen: <b>25 %</b></li>
                  <li>Peruutus &lt; 30 päivää ennen: <b>100 %</b></li>
                </ul>

                <p className="mt-3 font-semibold">Kesäsesonki (kesä–elokuu)</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Peruutus ≥ 120 päivää ennen: <b>ei maksua</b></li>
                  <li>Peruutus 91–119 päivää ennen: <b>25 %</b></li>
                  <li>Peruutus ≤ 90 päivää ennen: <b>100 %</b></li>
                </ul>

                <p className="mt-3 font-semibold">Maksu- ja palautuskäytäntö</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Peruutusmaksusta <b>vähennetään</b> maksettu varausmaksu.</li>
                  <li>Jos peruutusmaksu on varausmaksua suurempi, <b>laskutamme erotuksen</b>.</li>
                  <li>Jos peruutusmaksu on varausmaksua pienempi tai yhtä suuri, <b>lisämaksuja ei synny</b>.</li>
                  <li>Poikkeavat ehdot voidaan sopia erikseen; ne vahvistetaan kirjallisesti tarjouksessa/tilausvahvistuksessa.</li>
                </ul>

                <p className="mt-2">Muutoksista ja peruutuksista viestitään viipymättä ja aina Buukkerin kautta.</p>
              </div>

              {/* Jos esiintyjä peruu */}
              <div className="mt-6 text-black/80 dark:text-white/80">
                <h4 className="font-semibold mb-2">Jos esiintyjä peruu</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Peruutus tehdään <b>viipymättä ja kirjallisesti</b>.</li>
                  <li>Varaus <b>puretaan</b> ja asiakkaalle <b>palautetaan varausmaksu</b> kokonaisuudessaan.</li>
                  <li>Esiintyjä pyrkii Buukkerin kanssa <b>etsimään korvaavan esiintyjän</b> (mahdollisuuksien mukaan).</li>
                  <li>Toistuvat tai myöhäiset peruutukset voivat johtaa <b>näkyvyyden rajoittamiseen tai tilin jäädyttämiseen</b>.</li>
                </ul>
              </div>
            </section>

            {/* --- KÄYTTÖEHDOT (TÄYSI) --- */}
            <section id="ehdot" className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-5">
              <h2 className="text-xl font-semibold mb-1 flex items-center gap-2 text-black/90 dark:text-white/90">
                <FileText size={20} /> Käyttöehdot
              </h2>
              <p className="text-sm mb-4 text-black/60 dark:text-white/60">
                Voimassa alkaen 1.11.2025 — viimeksi päivitetty {new Date().toLocaleDateString("fi-FI")}
              </p>

              <Ehto title="1. Yleistiedot ja hyväksyminen">
                Buukkeri (“Palvelu”) on alusta tilaajien ja esiintyjien kohtaamiseen. Käyttämällä Palvelua hyväksyt nämä ehdot.
                Tilin luomisen yhteydessä annettu hyväksyntä <b>tallennetaan</b> tilillesi.
              </Ehto>

              <Ehto title="2. Määritelmät">Tilaaja, Esiintyjä, Buukkeri (ylläpitäjä/markkinapaikka).</Ehto>

              <Ehto title="3. Palvelun käyttö">
                Sitoudut lain- ja hyvän tavan mukaiseen käyttöön sekä ajantasaisten ja totuudenmukaisten tietojen ylläpitoon.
              </Ehto>

              <Ehto title="4. Varaukset ja sopimussuhde">
                Tilaajan ja Esiintyjän välinen sopimus syntyy, kun tilaaja painaa sähköpostissa <b>“Hyväksy tarjous”</b> -nappia.
                Samalla tilaaja hyväksyy Palvelun peruutusehdot. Hyväksynnästä tallennetaan varaukselle <b>aikaleima ja tunnistetiedot</b>.
                Tarjous on voimassa <b>1 tunnin</b> lomakkeen lähetyksestä. Buukkeri ei itse tuota esiintymistä, vaan tarjoaa varaus- ja viestintäalustan.
              </Ehto>

              <Ehto title="5. Hinnat, maksut ja välityspalkkio">
                Provisio sekä hyväksynnässä veloitettava <b>varausmaksu</b> lasketaan vain
                <b> esiintymispalkkiosta</b> (bilebändit, duot &amp; DJ:t 15&nbsp;%, trubaduurit 12&nbsp;%).
                Matkakulut ja esiintyjän myymät/vuokraamat <b>lisäpalvelut</b> eivät kuulu välityspalkkion
                tai varausmaksun piiriin — Buukkeri ei peri niistä palkkiota.
                Varausmaksu <b>hyvitetään</b> loppusummasta, mutta sitä <b>ei palauteta</b> asiakkaan
                peruuttaessa (peruutusmaksuista vähennetään maksettu varausmaksu).
                Loppusumma (palkkion loppu + mahdolliset lisäpalvelut) sovitaan ja maksetaan
                <b> suoraan esiintyjän ja asiakkaan</b> välillä; tarvittaessa Buukkeri voi avustaa
                laskutuksessa. Matkakulut lasketaan automaattisesti kotiosoitteesta edestakaisin
                (0,59&nbsp;€/km).
              </Ehto>

              <Ehto title="6. Alustan kiertäminen ja seuraamukset">
                Buukkerin kautta syntyneet kontaktit ja sopimukset hoidetaan Palvelussa. Alustan ohittaminen johtaa tilin
                välittömään sulkemiseen. Buukkeri voi periä vähintään välityspalkkion <b>suuruisen</b> sopimussakon (perustuen tapahtuman kokonaishintaan),
                käsittelykulut sekä korvauksen aiheutuneesta vahingosta.
              </Ehto>

              <Ehto title="7. Vastuunrajoitus">
                Palvelu tarjotaan ”sellaisena kuin se on”. Buukkeri ei vastaa keskeytyksistä eikä välillisistä vahingoista.
              </Ehto>

              <Ehto title="8. Käyttäjän sisältö ja oikeudet">
                Vastaat itse lisäämiesi sisältöjen oikeuksista. Myönnät Buukkerille ei-yksinomaisen oikeuden näyttää materiaaleja
                palvelun esittelytarkoituksiin.
              </Ehto>

              <Ehto title="9. Immateriaalioikeudet">
                Sivuston materiaalit ovat Buukkerin tai kumppaneiden omaisuutta. Luvaton kopiointi on kielletty.
              </Ehto>

              <Ehto title="10. Kielletty toiminta">
                Laiton tai loukkaava sisältö, roskaposti, haittakoodi ja luvaton henkilötietojen kerääminen on kiellettyä.
              </Ehto>

              <Ehto title="11. Tietosuoja ja evästeet">
                Henkilötietoja käsitellään tietosuojaselosteen mukaisesti. Sivusto käyttää evästeitä.
                Lue: <Link to="/tietosuoja" className="underline text-pink dark:text-primary">Tietosuojaseloste</Link>
              </Ehto>

              <Ehto title="12. Palvelun muutokset">
                Buukkeri voi päivittää Palvelua ja ehtoja ilmoittamalla niistä. Jatkamalla käyttöä hyväksyt muutokset.
              </Ehto>

              <Ehto title="13. Sovellettava laki ja riidanratkaisu">
                Suomen laki. Riidat pyritään ensisijaisesti ratkaisemaan neuvottelemalla, muutoin toimivaltainen yleinen tuomioistuin.
              </Ehto>

              <Ehto title="14. Yhteystiedot">
                Buukkeri, Kerava, Uusimaa — <a className="underline" href="mailto:info@buukkeri.fi">info@buukkeri.fi</a> —{" "}
                <a className="underline" href="tel:+358443519265">044&nbsp;351&nbsp;9265</a>
              </Ehto>
            </section>

          </main>
        </div>
      </div>
    </div>
  );
}

/* Pieni otsikoitu lohko käyttöehtojen sisällä */
function Ehto({ title, children }) {
  return (
    <section className="mt-5 first:mt-3">
      <h3 className="font-customFont2 dark:text-white/90 text-black/90 text-base md:text-lg font-semibold">
        {title}
      </h3>
      <div className="dark:text-white/80 text-black/80 text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}
